/**
 * Mother's Day GWP — Free Leggings Campaign
 * Handles: add, replace, duplicate prevention, empty-cart removal, single-item enforcement
 * Line property: _gwp: mothers-day-leggings
 *
 * Hardened: sequential cart ops, concurrency lock, double-click guard,
 *           cart page + drawer refresh, no listener stacking.
 */
(function () {
  'use strict';

  var GWP_KEY = '_gwp';
  var GWP_VAL = 'mothers-day-leggings';
  var busy = false; // global concurrency lock

  /* ── Helpers ── */

  function cartFetch(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' → ' + r.status);
      return r.json();
    });
  }

  function getCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  function isGWP(item) {
    return item.properties && item.properties[GWP_KEY] === GWP_VAL;
  }

  /**
   * Run an array of cart-mutation functions sequentially.
   * Each fn must return a Promise. We build them lazily to avoid race conditions.
   */
  function runSequential(fns) {
    return fns.reduce(function (chain, fn) {
      return chain.then(fn);
    }, Promise.resolve());
  }

  /* ── Product selector / image toggle ── */

  function initProductSelector() {
    var productSelect = document.querySelector('[data-gwp-product-select]');
    if (!productSelect) return;

    // Use a data flag to avoid stacking listeners
    if (productSelect.dataset.gwpBound) return;
    productSelect.dataset.gwpBound = '1';

    productSelect.addEventListener('change', function () {
      updateSizeOptions(this.value);
      updateProductImage(this.value);
    });

    // Init with current value
    updateSizeOptions(productSelect.value);
    updateProductImage(productSelect.value);
  }

  function updateSizeOptions(productKey) {
    var sizeSelect = document.querySelector('[data-gwp-size-select]');
    if (!sizeSelect) return;

    var options = sizeSelect.querySelectorAll('option');
    var firstVisible = null;

    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      if (opt.getAttribute('data-gwp-product') === productKey) {
        opt.hidden = false;
        opt.disabled = false;
        if (!firstVisible) firstVisible = opt;
      } else {
        opt.hidden = true;
        opt.disabled = true;
        opt.selected = false;
      }
    }

    if (firstVisible) firstVisible.selected = true;
  }

  function updateProductImage(productKey) {
    var imgs = document.querySelectorAll('[data-gwp-product-img]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.getAttribute('data-gwp-product-img') === productKey) {
        img.classList.add('active');
      } else {
        img.classList.remove('active');
      }
    }
  }

  /* ── Add / Replace GWP ── */

  function addGWP(variantId) {
    if (busy) return;
    busy = true;

    var btn = document.querySelector('[data-gwp-md-add]');
    if (btn) {
      btn.classList.add('loading');
      btn.disabled = true;
    }

    // 1. Remove any existing mothers-day GWP
    // 2. Add the new one
    // 3. Refresh UI
    removeExistingGWP()
      .then(function () {
        return cartFetch('/cart/add.js', {
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: { '_gwp': 'mothers-day-leggings' }
        });
      })
      .then(function () {
        return refreshAll();
      })
      .catch(function (err) {
        console.error('[GWP] Add error:', err);
        // Still refresh so UI is consistent
        return refreshAll();
      })
      .then(function () {
        busy = false;
      });
  }

  function removeExistingGWP() {
    return getCart().then(function (cart) {
      var gwpItems = cart.items.filter(isGWP);
      if (gwpItems.length === 0) return;

      var fns = gwpItems.map(function (item) {
        return function () {
          return cartFetch('/cart/change.js', { id: item.key, quantity: 0 });
        };
      });
      return runSequential(fns);
    });
  }

  /* ── Enforce: qty=1, no duplicates, remove if cart empty ── */

  function enforceGWPRules() {
    if (busy) return Promise.resolve();
    busy = true;

    return getCart()
      .then(function (cart) {
        var gwpItems = cart.items.filter(isGWP);
        if (gwpItems.length === 0) { busy = false; return; }

        var nonGwpItems = cart.items.filter(function (item) { return !isGWP(item); });
        var fns = [];

        // If cart only has GWP items and nothing else → remove GWP
        if (nonGwpItems.length === 0) {
          gwpItems.forEach(function (item) {
            fns.push(function () {
              return cartFetch('/cart/change.js', { id: item.key, quantity: 0 });
            });
          });
        } else {
          // Force quantity to 1
          gwpItems.forEach(function (item) {
            if (item.quantity > 1) {
              fns.push(function () {
                return cartFetch('/cart/change.js', { id: item.key, quantity: 1 });
              });
            }
          });

          // Remove duplicates — keep only the first
          if (gwpItems.length > 1) {
            gwpItems.slice(1).forEach(function (item) {
              fns.push(function () {
                return cartFetch('/cart/change.js', { id: item.key, quantity: 0 });
              });
            });
          }
        }

        if (fns.length === 0) { busy = false; return; }

        return runSequential(fns).then(function () {
          return refreshAll();
        });
      })
      .catch(function (err) {
        console.error('[GWP] Enforce error:', err);
      })
      .then(function () {
        busy = false;
      });
  }

  /* ── UI Refresh ── */

  function refreshAll() {
    var promises = [];

    // Refresh cart drawer if present
    var oldDrawer = document.querySelector('cart-drawer');
    if (oldDrawer) {
      promises.push(
        fetch('/?section_id=cart-drawer')
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var newDrawer = doc.querySelector('cart-drawer');
            if (newDrawer && oldDrawer) {
              oldDrawer.innerHTML = newDrawer.innerHTML;
              oldDrawer.setAttribute('open', '');
              oldDrawer.dispatchEvent(new Event('section:load', { bubbles: true }));
              document.dispatchEvent(new Event('shopify:section:load'));
            }
          })
      );
    }

    // Refresh cart page items if on /cart
    var mainCartItems = document.querySelector('#main-cart-items');
    if (mainCartItems) {
      var sectionId = mainCartItems.dataset.id;
      promises.push(
        fetch('/?section_id=main-cart-items')
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var newSection = doc.querySelector('#main-cart-items');
            if (newSection && mainCartItems) {
              mainCartItems.innerHTML = newSection.innerHTML;
            }
          })
      );
    }

    return Promise.all(promises).then(function () {
      bindAll();
    });
  }

  /* ── Bind UI events ── */

  function bindAll() {
    initProductSelector();

    var addBtn = document.querySelector('[data-gwp-md-add]');
    if (addBtn) {
      addBtn.onclick = function () {
        var sizeSelect = document.querySelector('[data-gwp-size-select]');
        if (!sizeSelect || !sizeSelect.value) return;
        addGWP(sizeSelect.value);
      };
    }
  }

  /* ── Global listeners (attached once) ── */

  // Enforce rules when cart quantities change or items are removed
  document.body.addEventListener('click', function (e) {
    if (
      e.target.closest('[data-cart-quantity-adjust]') ||
      e.target.closest('cart-remove-button')
    ) {
      setTimeout(function () {
        enforceGWPRules();
      }, 350);
    }
  });

  // Re-bind after Shopify section reloads (theme editor, AJAX updates)
  document.addEventListener('shopify:section:load', function () {
    bindAll();
    // Small delay to let the DOM settle, then enforce
    setTimeout(function () { enforceGWPRules(); }, 100);
  });

  // Watch cart drawer mutations to re-bind after external drawer updates
  // (e.g. other scripts updating the drawer)
  var cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    var debounceTimer;
    var observer = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        bindAll();
      }, 50);
    });
    observer.observe(cartDrawer, { childList: true, subtree: true });
  }

  // Init on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      enforceGWPRules();
      bindAll();
    });
  } else {
    enforceGWPRules();
    bindAll();
  }
})();
